/**
 * Keyboard behaviour for an overlay — the phone menu drawer, the history
 * sheet, the products sheet.
 *
 * Opening moves focus inside (the drawer is earlier in the document than the
 * button that opens it, so without this, Tab from the button walks away from
 * it); Tab and Shift+Tab stay inside while it is open; Escape closes it; and
 * closing returns focus to whatever opened it, so a keyboard user is not
 * dropped at the top of the page.
 */

import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useOverlayFocus(open: boolean, box: RefObject<HTMLElement | null>, onClose: () => void): void {
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const items = (): HTMLElement[] =>
      [...(box.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])].filter((el) => !el.closest('[hidden], [inert]'));

    // After paint, so the overlay's own autofocus (if any) wins.
    const frame = requestAnimationFrame(() => {
      if (!box.current?.contains(document.activeElement)) items()[0]?.focus();
    });

    const onKey = (e: KeyboardEvent): void => {
      // A control inside that used Escape itself (cancelling a rename, closing
      // a list) has already said so; the overlay stays.
      if (e.defaultPrevented) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        close.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const list = items();
      if (list.length === 0) return;
      const first = list[0]!;
      const last = list[list.length - 1]!;
      const here = document.activeElement;
      if (e.shiftKey && (here === first || !box.current?.contains(here))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (here === last || !box.current?.contains(here))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKey);
      // Back to whatever opened it, if it is still on the page.
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [open, box]);
}
